// src/components/RecipeEditor.jsx (or wherever it is)
import React, { useState, useMemo, useEffect } from 'react';
import {
  X,
  Search,
  Plus,
  Minus,
  Trash2,
  ChefHat,
  Package,
  ChevronRight,
  ChevronLeft,
  Check,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  FileText,
  Loader2,
  ArrowRight,
  Tag,
  Copy,
} from 'lucide-react';
import useRecipe from '@/hooks/useReciepe';

/**
 * Recipe Editor Component - With Variant Support
 *
 * Step 1: Select menu item from visual card grid
 * Step 1.5: Select variant (if menu item has variants)
 * Step 2: Build recipe with compact ingredient picker
 */

export default function RecipeEditor({ recipe, menuItems = [], duplicateData, onSave, onClose }) {
  const isEditing = !!recipe;
  const isDuplicating = !!duplicateData;
  const [currentStep, setCurrentStep] = useState(isEditing ? 2 : 1);

  // ⬅️ NEW: load inventory + uom via hook
  const {
    inventoryItems,
    uoms,
    masterLoading: inventoryLoading,
  } = useRecipe();

  // Step 1: Menu item selection
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [menuSearchQuery, setMenuSearchQuery] = useState('');

  // Step 2: Ingredients
  const [ingredients, setIngredients] = useState([]);
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [prepNotes, setPrepNotes] = useState('');

  // ⬅️ NEW: categories derived from dynamic inventory
const CATEGORIES = useMemo(() => {
  return [
    ...new Set(
      inventoryItems
        .map((item) => {
          if (!item.category) return null;

          // item.category is string (thanks to mapInventoryFromApi)
          return item.category;
        })
        .filter(Boolean)
    ),
  ];
}, [inventoryItems]);



  // Check if selected menu item has variants
  const hasVariants = selectedMenuItem?.variants && selectedMenuItem.variants.length > 0;

  // Initialize when editing
   // Initialize when editing
  useEffect(() => {
    if (recipe) {
      const menuItem = menuItems.find((item) => item.id === recipe.menuItemId);
      setSelectedMenuItem(
        menuItem || {
          id: recipe.menuItemId,
          name: recipe.menuItemName,
          categoryName: recipe.categoryName,
          basePrice: recipe.sellingPrice,
          variants: [],
        }
      );

      // If editing and has variantId, find and set the variant
      if (recipe.variantId && menuItem?.variants) {
        const variant = menuItem.variants.find((v) => v.id === recipe.variantId);
        setSelectedVariant(variant || null);
      }

      // 🔴 IMPORTANT: normalize ingredients with conversion data
      const normalizedIngredients = (recipe.ingredients || []).map((ing) => {
        // Get inventory item id
        const invId =
          ing.id ??
          (typeof ing.idInventoryItem === "object"
            ? ing.idInventoryItem.id
            : ing.idInventoryItem) ??
          ing.inventoryItemId ??
          null;

        // Look up inventory item to get its UoM data
        const inventoryItem = inventoryItems.find(item => item.id === invId);

        // Get the recipe ingredient's current UoM
        const recipeUomId = (typeof ing.idUom === "object" && ing.idUom !== null
          ? ing.idUom.id
          : ing.idUom) ?? ing.uomId ?? null;

        // Get UoM objects for calculations
        const inventoryUom = uoms.find(u => u.id === inventoryItem?.uomId);
        const recipeUom = uoms.find(u => u.id === recipeUomId);

        const inventoryUomFactor = inventoryUom?.conversionFactor ?? inventoryItem?.uomConversionFactor ?? 1;
        const currentUomFactor = recipeUom?.conversionFactor ?? 1;
        const inventoryUnitCost = inventoryItem?.unitCost ?? ing.unitCost ?? 0;

        // Calculate base cost per unit
        const baseCostPerUnit = inventoryUomFactor > 0
          ? inventoryUnitCost / inventoryUomFactor
          : inventoryUnitCost;

        // Calculate current unit cost based on selected UoM
        const unitCost = baseCostPerUnit * currentUomFactor;

        return {
          ...ing,
          id: invId,
          idInventoryItem: invId,
          idUom: recipeUomId,
          uom: recipeUom?.label ?? ing.uom ?? '',
          uomCategory: ing.uomCategory ?? inventoryItem?.uomCategory ?? null,
          // Conversion data
          inventoryUomId: inventoryItem?.uomId,
          inventoryUomFactor: inventoryUomFactor,
          inventoryUnitCost: inventoryUnitCost,
          baseCostPerUnit: baseCostPerUnit,
          currentUomFactor: currentUomFactor,
          unitCost: unitCost,
          totalCost: (ing.quantity ?? 0) * unitCost,
        };
      });

      setIngredients(normalizedIngredients);
      setPrepNotes(recipe.prepNotes || "");
    }
  }, [recipe, menuItems, inventoryItems, uoms]);


  // Pre-populate ingredients when duplicating and reaching step 2
  useEffect(() => {
    if (isDuplicating && currentStep === 2 && ingredients.length === 0) {
      const clonedIngredients = (duplicateData.ingredients || []).map((ing) => {
        const invId = (typeof ing.idInventoryItem === "object"
          ? ing.idInventoryItem.id
          : ing.idInventoryItem) ?? ing.id;

        // Look up inventory item to get its UoM data
        const inventoryItem = inventoryItems.find(item => item.id === invId);

        // Get the recipe ingredient's current UoM
        const recipeUomId = (typeof ing.idUom === "object" && ing.idUom !== null
          ? ing.idUom.id
          : ing.idUom) ?? ing.uomId ?? null;

        // Get UoM objects for calculations
        const inventoryUom = uoms.find(u => u.id === inventoryItem?.uomId);
        const recipeUom = uoms.find(u => u.id === recipeUomId);

        const inventoryUomFactor = inventoryUom?.conversionFactor ?? inventoryItem?.uomConversionFactor ?? 1;
        const currentUomFactor = recipeUom?.conversionFactor ?? 1;
        const inventoryUnitCost = inventoryItem?.unitCost ?? ing.unitCost ?? 0;

        // Calculate base cost per unit
        const baseCostPerUnit = inventoryUomFactor > 0
          ? inventoryUnitCost / inventoryUomFactor
          : inventoryUnitCost;

        // Calculate current unit cost based on selected UoM
        const unitCost = baseCostPerUnit * currentUomFactor;

        return {
          ...ing,
          id: invId,
          idInventoryItem: invId,
          ingredientRowId: null, // clear for new creation
          idUom: recipeUomId,
          uom: recipeUom?.label ?? ing.uom ?? '',
          uomCategory: ing.uomCategory ?? inventoryItem?.uomCategory ?? null,
          // Conversion data
          inventoryUomId: inventoryItem?.uomId,
          inventoryUomFactor: inventoryUomFactor,
          inventoryUnitCost: inventoryUnitCost,
          baseCostPerUnit: baseCostPerUnit,
          currentUomFactor: currentUomFactor,
          unitCost: unitCost,
          totalCost: (ing.quantity ?? 0) * unitCost,
        };
      });

      setIngredients(clonedIngredients);
      setPrepNotes(duplicateData.prepNotes || "");
    }
  }, [isDuplicating, currentStep, duplicateData, ingredients.length, inventoryItems, uoms]);


  // Filter menu items
  const filteredMenuItems = useMemo(() => {
    if (!menuSearchQuery) return menuItems;
    const query = menuSearchQuery.toLowerCase();
    return menuItems.filter(item =>
      item.name?.toLowerCase().includes(query) ||
      item.categoryName?.toLowerCase().includes(query)
    );
  }, [menuItems, menuSearchQuery]);

  // ⬅️ CHANGED: Filter inventory items from hook
  const filteredInventoryItems = useMemo(() => {
    let items = inventoryItems;
    if (selectedCategory !== 'All') {
      items = items.filter(item => item.category === selectedCategory);
    }
    if (ingredientSearch) {
      const query = ingredientSearch.toLowerCase();
      items = items.filter(item =>
        item.name.toLowerCase().includes(query) ||
        (item.category || '').toLowerCase().includes(query)
      );
    }
    return items;
  }, [ingredientSearch, selectedCategory, inventoryItems]);

  // Get selling price based on variant or base price
  const sellingPrice = useMemo(() => {
    if (selectedVariant) {
      return selectedVariant.variantPrice || selectedMenuItem?.basePrice || 0;
    }
    return selectedMenuItem?.basePrice || 0;
  }, [selectedMenuItem, selectedVariant]);

  // Calculate costs
  const totalCost = useMemo(() => {
    return ingredients.reduce((sum, ing) => sum + (ing.totalCost || 0), 0);
  }, [ingredients]);

  const foodCostPercentage = sellingPrice > 0 ? (totalCost / sellingPrice) * 100 : 0;
  const grossProfit = sellingPrice - totalCost;

  const getFoodCostStatus = () => {
    if (foodCostPercentage <= 30) return 'good';
    if (foodCostPercentage <= 35) return 'warning';
    return 'danger';
  };

  const isIngredientAdded = (id) => ingredients.some(ing => ing.id === id);

const handleAddIngredient = (inventoryItem) => {
  if (isIngredientAdded(inventoryItem.id)) return;

  // Get inventory item's UoM conversion factor (from UoM lookup or from inventory item itself)
  const inventoryUom = uoms.find(u => u.id === inventoryItem.uomId);
  const inventoryUomFactor = inventoryUom?.conversionFactor ?? inventoryItem.uomConversionFactor ?? 1;

  // Calculate base cost per unit (cost per base unit like gram/ml/piece)
  // If inventory is in KG (factor=1000) at ₹50, base cost = ₹50/1000 = ₹0.05 per gram
  const baseCostPerUnit = inventoryUomFactor > 0
    ? inventoryItem.unitCost / inventoryUomFactor
    : inventoryItem.unitCost;

  // Initial quantity in inventory's UoM
  const initialQuantity = 1;
  const initialTotalCost = initialQuantity * inventoryItem.unitCost;

  setIngredients((prev) => [
    ...prev,
    {
      id: inventoryItem.id,
      idInventoryItem: inventoryItem.id,
      idUom: inventoryItem.uomId ?? null,
      name: inventoryItem.name,
      quantity: initialQuantity,
      uom: inventoryUom?.label ?? inventoryItem.uom,  // label like "kg"
      uomCategory: inventoryItem.uomCategory,          // category for filtering UoMs
      // Store original inventory UoM info for conversion
      inventoryUomId: inventoryItem.uomId,
      inventoryUomFactor: inventoryUomFactor,
      inventoryUnitCost: inventoryItem.unitCost,       // cost per inventory UoM (e.g., ₹50/kg)
      baseCostPerUnit: baseCostPerUnit,                // cost per base unit (e.g., ₹0.05/g)
      // Current UoM factor (initially same as inventory)
      currentUomFactor: inventoryUomFactor,
      // Calculated costs
      unitCost: inventoryItem.unitCost,                // cost per current UoM
      totalCost: initialTotalCost,
    },
  ]);
};



  const handleUpdateQuantity = (id, newQuantity) => {
    const quantity = Math.max(0, newQuantity);
    setIngredients(prev => prev.map(ing => {
      if (ing.id !== id) return ing;
      return { ...ing, quantity, totalCost: quantity * ing.unitCost };
    }));
  };

  const handleIncrementQuantity = (id, delta) => {
    setIngredients(prev => prev.map(ing => {
      if (ing.id !== id) return ing;
      const newQuantity = Math.max(0, ing.quantity + delta);
      return { ...ing, quantity: newQuantity, totalCost: newQuantity * ing.unitCost };
    }));
  };

 const handleUpdateUom = (ingredientId, uomId) => {
  setIngredients((prev) =>
    prev.map((ing) => {
      if (ing.id !== ingredientId) return ing;

      const uomObj = uoms.find((u) => String(u.id) === String(uomId));
      const newUomFactor = uomObj?.conversionFactor ?? 1;

      // Recalculate unit cost for new UoM
      // unitCost = baseCostPerUnit * newUomFactor
      // Example: if baseCost is ₹0.05/g and selecting KG (factor=1000), unitCost = ₹50/kg
      const newUnitCost = (ing.baseCostPerUnit ?? ing.unitCost) * newUomFactor;
      const newTotalCost = ing.quantity * newUnitCost;

      return {
        ...ing,
        idUom: uomObj?.id ?? null,
        uom: uomObj?.label ?? ing.uom,
        currentUomFactor: newUomFactor,
        unitCost: newUnitCost,
        totalCost: newTotalCost,
      };
    })
  );
};


  const handleRemoveIngredient = (id) => {
    setIngredients(prev => prev.filter(ing => ing.id !== id));
  };

  // Handle menu item selection
  const handleMenuItemSelect = (item) => {
    setSelectedMenuItem(item);
    setSelectedVariant(null); // Reset variant when menu item changes
  };

  // Handle continue from step 1
  const handleContinueFromStep1 = () => {
    if (!selectedMenuItem) return;

    // If item has variants, show variant selection
    if (hasVariants) {
      setCurrentStep(1.5);
    } else {
      setCurrentStep(2);
    }
  };

  // Handle continue from variant selection
  const handleContinueFromVariantSelection = () => {
    if (!selectedVariant) return;
    setCurrentStep(2);
  };

  const handleSave = () => {
    if (!selectedMenuItem || ingredients.length === 0) return;

    // Build recipe name with variant
    const recipeName = selectedVariant
      ? `${selectedMenuItem.name} - ${selectedVariant.variantName}`
      : selectedMenuItem.name;

    onSave({
      id: recipe?.id,
      menuItemId: selectedMenuItem.id,
      menuItemName: recipeName,
      variantId: selectedVariant?.id || null,
      variantName: selectedVariant?.variantName || null,
      categoryId: selectedMenuItem.categoryId,
      categoryName: selectedMenuItem.categoryName,
      sellingPrice,
      ingredients,
      totalCost,
      prepNotes,
      createdAt: recipe?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  };

  // Get display name for header
  const getDisplayName = () => {
    if (!selectedMenuItem) return '';
    if (selectedVariant) {
      return `${selectedMenuItem.name} - ${selectedVariant.variantName}`;
    }
    return selectedMenuItem.name;
  };

  // Step 1: Menu Item Selection
  const renderStep1 = () => (
    <div className="flex flex-col h-full">
      {/* Duplicate Info Banner */}
      {isDuplicating && (
        <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
          <Copy size={14} className="text-blue-500" />
          <span className="text-sm text-blue-700">
            Duplicating from: <span className="font-medium">{duplicateData.sourceRecipeName}</span>
          </span>
          <span className="text-xs text-blue-500 ml-auto">
            {duplicateData.ingredients.length} ingredients will be copied
          </span>
        </div>
      )}

      {/* Search */}
      <div className="px-4 py-3 border-b border-slate-100">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search menu items..."
            value={menuSearchQuery}
            onChange={(e) => setMenuSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            autoFocus
          />
        </div>
      </div>

      {/* Menu Items Grid */}
      <div className="flex-1 overflow-y-auto p-4">
        {menuItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 size={32} className="text-teal-500 animate-spin mb-3" />
            <p className="text-slate-500 text-sm">Loading menu items...</p>
          </div>
        ) : filteredMenuItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Package size={40} className="text-slate-300 mb-3" />
            <p className="text-slate-500 text-sm">No menu items found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
            {filteredMenuItems.map((item) => {
              const isSelected = selectedMenuItem?.id === item.id;
              const variantCount = item.variants?.length || 0;

              return (
                <button
                  key={item.id}
                  onClick={() => handleMenuItemSelect(item)}
                  className={`group relative p-3 rounded-lg border-2 text-left transition-all ${
                    isSelected
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-slate-200 hover:border-teal-300 hover:bg-slate-50'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                      <Check size={12} className="text-white" />
                    </div>
                  )}
                  <h4 className="font-medium text-slate-900 text-sm truncate pr-6">{item.name}</h4>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-slate-500">{item.categoryName}</p>
                    <p className="text-sm font-bold text-teal-600">₹{item.basePrice?.toFixed(0)}</p>
                  </div>
                  {/* Variant indicator */}
                  {variantCount > 0 && (
                    <div className="mt-2 flex items-center gap-1">
                      <Tag size={10} className="text-blue-500" />
                      <span className="text-[10px] text-blue-600 font-medium">
                        {variantCount} variant{variantCount > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
        <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg text-sm">
          Cancel
        </button>
        <button
          onClick={handleContinueFromStep1}
          disabled={!selectedMenuItem}
          className="flex items-center gap-1.5 px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
        >
          {hasVariants ? 'Select Variant' : 'Continue'} <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );

  // Step 1.5: Variant Selection
  const renderVariantSelection = () => (
    <div className="flex flex-col h-full">
      {/* Header Info */}
      <div className="px-4 py-4 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
            <ChefHat size={20} className="text-teal-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{selectedMenuItem?.name}</h3>
            <p className="text-xs text-slate-500">{selectedMenuItem?.categoryName}</p>
          </div>
        </div>
      </div>

      {/* Variant Selection */}
      <div className="flex-1 overflow-y-auto p-4">
        <p className="text-sm text-slate-600 mb-4">
          This item has multiple variants. Select which variant you want to create a recipe for:
        </p>

        <div className="space-y-2">
          {selectedMenuItem?.variants?.map((variant) => {
            const isSelected = selectedVariant?.id === variant.id;

            return (
              <button
                key={variant.id}
                onClick={() => setSelectedVariant(variant)}
                className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                  isSelected
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-slate-200 hover:border-teal-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  {isSelected ? (
                    <div className="w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                      <Check size={12} className="text-white" />
                    </div>
                  ) : (
                    <div className="w-5 h-5 border-2 border-slate-300 rounded-full" />
                  )}
                  <div className="text-left">
                    <p className="font-medium text-slate-900">{variant.variantName}</p>
                    <p className="text-xs text-slate-500">
                      Recipe for {selectedMenuItem?.name} - {variant.variantName}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-teal-600">₹{variant.variantPrice?.toFixed(0)}</p>
                  <p className="text-[10px] text-slate-400 uppercase">Selling Price</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
        <button
          onClick={() => setCurrentStep(1)}
          className="flex items-center gap-1 px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg text-sm"
        >
          <ChevronLeft size={16} /> Back
        </button>
        <button
          onClick={handleContinueFromVariantSelection}
          disabled={!selectedVariant}
          className="flex items-center gap-1.5 px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
        >
          Continue <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );

  // Step 2: Ingredient Builder - Compact Layout
  const renderStep2 = () => {
    const foodCostStatus = getFoodCostStatus();

    return (
      <div className="flex flex-col h-full">
        {/* Main Content - Split Panel */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Panel: Available Ingredients */}
          <div className="w-[280px] flex flex-col border-r border-slate-200 bg-slate-50">
            {/* Search & Category */}
            <div className="p-3 border-b border-slate-200 bg-white space-y-2">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={ingredientSearch}
                  onChange={(e) => setIngredientSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-teal-500"
                />
              </div>
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setSelectedCategory('All')}
                  className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                    selectedCategory === 'All' ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  All
                </button>
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                      selectedCategory === cat ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Compact Ingredient List */}
            <div className="flex-1 overflow-y-auto p-2">
              {inventoryLoading ? (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <Loader2 className="animate-spin mb-2" size={20} />
                  <p className="text-xs text-slate-500">Loading inventory...</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredInventoryItems.map((item) => {
                    const isAdded = isIngredientAdded(item.id);
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleAddIngredient(item)}
                        disabled={isAdded}
                        className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-left transition-all ${
                          isAdded
                            ? 'bg-teal-50 border border-teal-200 opacity-50'
                            : 'bg-white border border-slate-200 hover:border-teal-400 hover:bg-teal-50'
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-slate-900 truncate">{item.name}</p>
                          <p className="text-[10px] text-slate-400">₹{item.unitCost}/{item.uom}</p>
                        </div>
                        {isAdded ? (
                          <Check size={14} className="text-teal-500 ml-2" />
                        ) : (
                          <Plus size={14} className="text-slate-400 ml-2" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel: Recipe Builder */}
          <div className="flex-1 flex flex-col bg-white">
            {/* Compact Header with Cost Summary */}
            <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
              <div className="flex items-center justify-between">
                {/* Menu Item Info */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-500 flex items-center justify-center">
                    <ChefHat size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{getDisplayName()}</h3>
                    <p className="text-xs text-slate-500">
                      {selectedMenuItem?.categoryName} • Selling: ₹{sellingPrice.toFixed(0)}
                    </p>
                  </div>
                </div>

                {/* Inline Cost Summary */}
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-[10px] text-slate-400 uppercase">Cost</p>
                    <p className="text-sm font-bold text-slate-900">₹{totalCost.toFixed(2)}</p>
                  </div>
                  <div className={`px-3 py-1.5 rounded-lg ${
                    foodCostStatus === 'good' ? 'bg-green-50' :
                    foodCostStatus === 'warning' ? 'bg-amber-50' : 'bg-red-50'
                  }`}>
                    <p className="text-[10px] text-slate-400 uppercase">Food %</p>
                    <p className={`text-sm font-bold flex items-center gap-0.5 ${
                      foodCostStatus === 'good' ? 'text-green-600' :
                      foodCostStatus === 'warning' ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {foodCostStatus === 'good' ? <TrendingDown size={12} /> : <TrendingUp size={12} />}
                      {foodCostPercentage.toFixed(1)}%
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-slate-400 uppercase">Profit</p>
                    <p className={`text-sm font-bold ${grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ₹{grossProfit.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Ingredients List - Compact Rows */}
            <div className="flex-1 overflow-y-auto">
              {ingredients.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                  <Package size={40} className="text-slate-200 mb-3" />
                  <p className="text-slate-500 text-sm">Click ingredients on the left to add</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {ingredients.map((ing) => (
                    <div key={ing.id} className="flex items-center gap-2 px-4 py-2 hover:bg-slate-50">
                      {/* Name */}
                      <div className="w-32 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{ing.name}</p>
                      </div>

                      {/* Quantity Control */}
                      <div className="flex items-center gap-0.5 bg-slate-100 rounded-md">
                        <button
                          onClick={() => handleIncrementQuantity(ing.id, -10)}
                          className="w-6 h-6 flex items-center justify-center hover:bg-slate-200 rounded-l-md text-slate-600"
                        >
                          <Minus size={12} />
                        </button>
                        <input
                          type="number"
                          value={ing.quantity}
                          onChange={(e) => handleUpdateQuantity(ing.id, parseFloat(e.target.value) || 0)}
                          className="w-14 px-1 py-1 text-center text-xs font-medium bg-white border-y border-slate-200 focus:outline-none"
                          min="0"
                        />
                        <button
                          onClick={() => handleIncrementQuantity(ing.id, 10)}
                          className="w-6 h-6 flex items-center justify-center hover:bg-slate-200 rounded-r-md text-slate-600"
                        >
                          <Plus size={12} />
                        </button>
                      </div>

                      {/* UOM - filtered by ingredient's category */}
                    <select
  value={ing.idUom ?? ""}   // store the numeric id
  onChange={(e) => handleUpdateUom(ing.id, e.target.value)}
  className="w-20 px-1 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
  title={ing.uomCategory ? `Only ${ing.uomCategory} units available` : 'Select unit'}
>
  <option value="" disabled>Select</option>
  {uoms
    .filter(uom => {
      // If ingredient has a category, only show matching UoMs
      if (ing.uomCategory && uom.uomCategory) {
        return uom.uomCategory === ing.uomCategory;
      }
      // If no category info, show all UoMs
      return true;
    })
    .map((uom) => (
    <option key={uom.id} value={uom.id}>
      {uom.label}
    </option>
  ))}
</select>


                      {/* Rate */}
                      <span className="text-[10px] text-slate-400 w-16">@₹{ing.unitCost}/{ing.uom}</span>

                     {/* Cost */}
<span className="text-sm font-semibold text-slate-900 w-16 text-right">
  ₹{(ing.quantity * ing.unitCost).toFixed(2)}
</span>


                      {/* Remove */}
                      <button
                        onClick={() => handleRemoveIngredient(ing.id)}
                        className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Warning if high cost */}
            {foodCostStatus !== 'good' && ingredients.length > 0 && (
              <div className={`mx-4 mb-2 px-3 py-2 rounded-lg flex items-center gap-2 text-xs ${
                foodCostStatus === 'warning' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
              }`}>
                <AlertCircle size={14} />
                {foodCostStatus === 'warning' ? 'Food cost 30-35% - consider optimizing' : 'Food cost >35% - review recipe'}
              </div>
            )}

            {/* Prep Notes - Collapsible inline */}
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
              <div className="flex items-center gap-2">
                <FileText size={14} className="text-slate-400" />
                <input
                  type="text"
                  value={prepNotes}
                  onChange={(e) => setPrepNotes(e.target.value)}
                  placeholder="Add preparation notes..."
                  className="flex-1 text-xs bg-transparent border-0 focus:outline-none focus:ring-0 text-slate-600 placeholder-slate-400"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-4 py-3 border-t border-slate-200 bg-white flex items-center justify-between">
          <button
            onClick={() => {
              if (isEditing) {
                onClose();
              } else if (hasVariants) {
                setCurrentStep(1.5);
              } else {
                setCurrentStep(1);
              }
            }}
            className="flex items-center gap-1 px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm"
          >
            {isEditing ? 'Cancel' : <><ChevronLeft size={16} /> Back</>}
          </button>
          <button
            onClick={handleSave}
            disabled={ingredients.length === 0}
            className={`flex items-center gap-1.5 px-5 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium ${
              isDuplicating ? 'bg-blue-600 hover:bg-blue-700' : 'bg-teal-600 hover:bg-teal-700'
            }`}
          >
            <Check size={16} />
            {isEditing ? 'Update Recipe' : isDuplicating ? 'Save Duplicate' : 'Save Recipe'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col overflow-hidden">
        {/* Single Compact Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isDuplicating ? 'bg-blue-500' : 'bg-teal-500'}`}>
              {isDuplicating ? <Copy size={18} className="text-white" /> : <ChefHat size={18} className="text-white" />}
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">
                {isEditing ? 'Edit Recipe' : isDuplicating ? 'Duplicate Recipe' : 'New Recipe'}
              </h2>
              {!isEditing && (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                    currentStep === 1
                      ? isDuplicating ? 'bg-blue-100 text-blue-700' : 'bg-teal-100 text-teal-700'
                      : 'bg-slate-100 text-slate-400'
                  }`}>
                    1. Select Item
                  </span>
                  <ChevronRight size={12} className="text-slate-300" />
                  {hasVariants && (
                    <>
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                        currentStep === 1.5
                          ? isDuplicating ? 'bg-blue-100 text-blue-700' : 'bg-teal-100 text-teal-700'
                          : 'bg-slate-100 text-slate-400'
                      }`}>
                        2. Select Variant
                      </span>
                      <ChevronRight size={12} className="text-slate-300" />
                    </>
                  )}
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                    currentStep === 2
                      ? isDuplicating ? 'bg-blue-100 text-blue-700' : 'bg-teal-100 text-teal-700'
                      : 'bg-slate-100 text-slate-400'
                  }`}>
                    {hasVariants ? '3' : '2'}. Add Ingredients
                  </span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {currentStep === 1 && renderStep1()}
          {currentStep === 1.5 && renderVariantSelection()}
          {currentStep === 2 && renderStep2()}
        </div>
      </div>
    </div>
  );
}
